import React from 'react';
import PropTypes from 'prop-types';
import { R } from '@leverege/value-resolver';
import Theme from "./Theme.js";
import Popup, { Item } from "./Popup.js";
import TextInput from "./TextInput.js";
import S from './Selector.css';
import PopupStyle from './Popup.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const IS_ALPHANUM_REGEX = /[a-zA-Z0-9 ]/;

/**
 * React @class Selector renders a component that allows a user to choose among several items
 * and also filter those items if the Selector is configured to do so.
 * @property {Array.<object>} values An array of `{ nameKey : name, valueKey : value }` objects among which to select.
 * @property {string} nameKey The key in an element of the `values` array of an element's name.
 * The default is 'name'. This name is what's displayed and what's filtered on.
 * @property {string} valueKey The key in an element of the `values` array of an element's value. The default is 'value'.
 * @property {string} title The title of the Popup.
 * @property {any} eventData Whatever you want to pass through the `onChange` function when a user selects an item.
 * @property {string} targetAttachment Where to attach the target element.
 * @property {string} attachment Where to attach the Popup.
 * @property {function} renderer An optional custom renderer that produces the visible content within the <Popup.Item />.
 * @property {boolean} showFilter Whether or not to render a Selector filter / search bar.
 * @property {string} filterVariant A variant for the filter input.
 * @property {object} filterOptions An options object by which to configure the filter feature.
 * @property {string} filterOptions.placeholder The `placeholder` text for the filter input. Defaults to 'Search . . .'.
 * @property {string} filterOptions.noResults The text to show in the filter input. Defaults to 'No Results'.
 */
export default class Selector extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filterTerm: ''
    };
    this.toggleRef = /*#__PURE__*/React.createRef();
    this.popupRef = /*#__PURE__*/React.createRef();
    this.lastAlphaInput = '';
    this.inputDebounceTimer = null;
  }
  static contextType = Theme.Context;
  static defaultProps = {
    targetAttachment: 'bottom right',
    attachment: 'top right',
    showFilter: false,
    filterOptions: {
      placeholder: 'Search . . .',
      noResults: 'No Results'
    },
    eventData: null,
    nameKey: 'name',
    valueKey: 'value'
  };
  static propTypes = {
    values: PropTypes.array.isRequired,
    eventData: PropTypes.any,
    targetAttachment: PropTypes.string,
    attachment: PropTypes.string,
    showFilter: PropTypes.bool,
    filterOptions: PropTypes.object,
    nameKey: PropTypes.string,
    valueKey: PropTypes.string
  };
  valuesType() {
    const {
      values
    } = this.props;
    return values.reduce((acc, cur, i) => {
      const t = typeof cur;
      if (i === 0) {
        return t;
      }
      if (acc === t) {
        return acc;
      }
      return 'object';
    }, null);
  }
  componentDidMount() {
    if (this.toggleRef.current) {
      this.addEventListeners(this.toggleRef.current);
    }
  }
  addEventListeners(toggle) {
    toggle.addEventListener('keydown', this.onAlphaKey);
  }
  onAlphaKey = evt => {
    if (evt.code === 'Space') {
      evt.preventDefault();
    }
    if (evt.key.length !== 1) {
      return;
    }
    if (IS_ALPHANUM_REGEX.test(evt.key)) {
      this.lastAlphaInput += evt.key;
      clearTimeout(this.inputDebounceTimer);
      this.inputDebounceTimer = setTimeout(() => {
        this.onSelectByLabel(this.lastAlphaInput);
        this.lastAlphaInput = '';
      }, 750);
    }
  };
  onSelectByLabel = label => {
    const {
      values,
      nameKey,
      valueKey
    } = this.props;
    const vt = this.valuesType();
    const found = values.find(value => {
      const name = vt === 'object' ? value[nameKey] : value;
      return name?.toLowerCase?.()?.startsWith(label?.toLowerCase());
    });
    if (found) {
      this.onSelect({
        value: found[valueKey]
      });
      setTimeout(() => {
        const items = this.popupRef.current?.querySelectorAll(`.${PopupStyle.item}`);
        // eslint-disable-next-line no-restricted-syntax
        for (const item of items) {
          if (item.innerText === found[nameKey]) {
            item.focus();
            item.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            break;
          }
        }
      }, 3);
    }
  };

  /**
   * @method onSelect Fire when the user selects an item. If given an onChange function, fire that as a side effect passing through eventData.
   * @param {object} evt
   */
  onSelect = evt => {
    const {
      onChange,
      eventData
    } = this.props;
    if (onChange) {
      onChange({
        value: evt.value,
        data: eventData
      });
    }
  };

  /**
   * @method onChangePopup
   * @param {object} evt
   * @param {boolean} evt.isOpened Whether or not the Popup is open on a given change
   */
  onChangePopup = ({
    isOpened
  }) => {
    const {
      onChangePopup,
      showFilter
    } = this.props;
    if (onChangePopup) {
      onChangePopup({
        isOpened
      });
    } else if (showFilter && !isOpened) {
      this.setState({
        filterTerm: ''
      });
    }
  };

  /**
   * @method onChangeFilter Updates the filterTerm when the user changes it.
   * @param {object} evt The event containing the input filter term value
   */
  onChangeFilter = evt => {
    const {
      onChangeFilter
    } = this.props;
    if (onChangeFilter) {
      onChangeFilter(evt);
    } else {
      this.setState({
        filterTerm: evt.value.trim().toLowerCase()
      });
    }
  };

  /**
   * @method filter Filters the values by some user input filterTerm
   * @param {Array.<object>} values An array of `{ nameKey : name, valueKey : value }` objects among which to select.
   * @param {string} filterTerm The term for which to filter the `values`.
   * @param {string} nameKey The attribute of an element of the `values` array on which to filter using the `filterTerm`.
   * @returns {array} `values` filtered using the `filterTerm` on each element's `value[nameKey]` attribute.
   */
  filter = (values, filterTerm, nameKey) => {
    const vt = this.valuesType();
    return values.filter(value => {
      const v = vt === 'object' ? value[nameKey] : value;
      const isString = typeof v === 'string';
      return isString && v.toLowerCase().includes(filterTerm);
    });
  };
  onScroll = e => {
    const {
      onBottom
    } = this.props;
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && typeof onBottom === 'function') {
      onBottom();
    }
  };
  render() {
    const theme = this.context;
    const {
      filterTerm
    } = this.state;
    const {
      loading,
      values,
      eventData,
      value,
      nameKey,
      valueKey,
      renderer,
      onBottom,
      onChange,
      onChangePopup,
      onChangeFilter,
      icon,
      title,
      showFilter,
      stickyFilter = true,
      filterVariant,
      filterOptions,
      toggleVariant,
      toggleAttributes,
      checkStyle = 'radio',
      placeholder = '',
      ...rest
    } = theme.props('Selector', this.props); // eslint-disable-line

    const {
      t
    } = Theme.I18N.ns('Selector');
    if (!Array.isArray(values)) {
      return null;
    }
    const vt = this.valuesType();
    const cValue = values.find(item => {
      return vt === 'object' ? value == null ? item[valueKey] == null : item[valueKey] === value : value === item;
    });
    const name = vt === 'object' ? cValue && cValue[nameKey] : cValue ?? placeholder;
    let renderValues = values;
    if (showFilter) {
      renderValues = this.filter(values, filterTerm, nameKey);
    }
    let cTitle = title || name;
    if (typeof cTitle === 'function') {
      cTitle = cTitle(cValue, values);
    }
    let cIcon = icon;
    if (typeof cIcon === 'function') {
      cIcon = cIcon(cValue, values);
    }
    let ta;
    if (toggleAttributes) {
      ta = {
        ...toggleAttributes,
        domRef: this.toggleRef
      };
    } else {
      ta = {
        domRef: this.toggleRef
      };
    }
    return /*#__PURE__*/_jsxs(Popup, {
      domRef: this.popupRef,
      selector: this,
      title: cTitle,
      icon: cIcon,
      toggleVariant: toggleVariant || 'selector',
      toggleAttributes: ta,
      setPopupMinWidth: true,
      onScroll: onBottom ?? this.onScroll,
      onChange: this.onChangePopup,
      ...rest,
      children: [showFilter && /*#__PURE__*/_jsx(Popup.Header, {
        sticky: stickyFilter,
        children: /*#__PURE__*/_jsx(TextInput, {
          variant: filterVariant,
          className: S.filterInput,
          onChange: this.onChangeFilter,
          value: filterTerm,
          changeOnReturn: false,
          alwaysFireOnReturn: false,
          name: "selectorFilter",
          hint: t('placeholder', filterOptions.placeholder)
        })
      }), renderValues.map(val => {
        const v = vt === 'object' ? val[valueKey] : val;
        const checked = value == null ? v === null : v === value;
        const name = R(vt === 'object' ? val[nameKey] : val);
        const view = renderer == null ? name : renderer(val, name, checked);
        if (view?.type?.prototype === Item.prototype) {
          return /*#__PURE__*/React.cloneElement(view, {
            checkStyle,
            checked,
            key: `Item-${v}`,
            val: v,
            onClick: this.onSelect
          });
        }
        return /*#__PURE__*/_jsx(Popup.Item, {
          checkStyle: checkStyle,
          checked: checked,
          value: v,
          onClick: this.onSelect,
          children: view
        }, `Item-${v}`);
      }), showFilter && renderValues.length === 0 && /*#__PURE__*/_jsx(Popup.Item, {
        children: t('noResults', filterOptions.noResults)
      })]
    });
  }
}